var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo } from "react";
import { TextLink } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { getContributionDisplayName, getMainTitleName, getTitleCoverUrl, } from "../../utils/titleDisplay";
import { getPersonDetailPath } from "../../utils/personNavigation";
import { TitleCoverImage } from "../../components/title/TitleCoverImage";
import { TitleNamesTable } from "../../components/TitleNamesTable";
import { TitleContributionsTable } from "../../components/TitleContributionsTable";
import { TitleSupportingResourcesTable } from "../../components/TitleSupportingResourcesTable";
import TitleProductsPanel from "../../components/title/TitleProductsPanel";
import { InfoBox, InfoBoxTypeEnum } from "../../components/InfoBox";
import { CopyableId } from "../../components/CopyableId";
import { PublicCatalogLink } from "../../components/PublicCatalogLink";
import { TitleIssnMediumWarning } from "../../components/title/TitleIssnMediumWarning";
import { usePermissions } from "../../hooks/usePermissions";
import { formatCollectionOptionLabel } from "../../utils/collectionDisplayUtils";
import css from "./TitleDetail.module.css";
var Fact = function (_a) {
    var labelId = _a.labelId, value = _a.value, wide = _a.wide;
    if (value === null || value === undefined || value === "") {
        return null;
    }
    return (React.createElement("div", { className: wide ? css.factWide : undefined },
        React.createElement("div", { className: css.factLabel },
            React.createElement(FormattedMessage, { id: labelId })),
        React.createElement("div", { className: css.factValue }, value)));
};
var DetailSection = function (_a) {
    var labelId = _a.labelId, count = _a.count, children = _a.children;
    return (React.createElement("section", { className: css.section },
        React.createElement("h3", { className: css.sectionHeading },
            React.createElement(FormattedMessage, { id: labelId }),
            count !== undefined && (React.createElement("span", { className: css.sectionCount },
                "(",
                count,
                ")"))),
        children));
};
var formatSubject = function (subject) {
    if (subject.text && subject.code) {
        return "".concat(subject.text, " (").concat(subject.code, ")");
    }
    return subject.text || subject.code || "";
};
var TitleDetail = function (_a) {
    var _b, _c, _d, _e;
    var title = _a.title;
    var history = useHistory();
    var intl = useIntl();
    var isTitleReadOnlyCoPublisher = usePermissions().isTitleReadOnlyCoPublisher;
    var isReadOnlyCoPublisher = isTitleReadOnlyCoPublisher(title);
    var handlePersonClick = useCallback(function (personId, legalForm) {
        var path = getPersonDetailPath(personId, legalForm);
        if (path) {
            history.push(path);
        }
    }, [history]);
    var mainName = useMemo(function () { return getMainTitleName(title.names); }, [title.names]);
    var otherNames = useMemo(function () { return (title.names || []).filter(function (name) { return name !== mainName; }); }, [title.names, mainName]);
    var coverUrl = useMemo(function () { return getTitleCoverUrl(title.supportingResources); }, [title.supportingResources]);
    var supportingResources = useMemo(function () { return title.supportingResources || []; }, [title.supportingResources]);
    var contributions = useMemo(function () {
        return __spreadArray([], (title.titleContributions || []), true).sort(function (a, b) { var _a, _b; return ((_a = a.sequenceNumber) !== null && _a !== void 0 ? _a : 0) - ((_b = b.sequenceNumber) !== null && _b !== void 0 ? _b : 0); });
    }, [title.titleContributions]);
    var bylineContributions = useMemo(function () {
        return contributions.filter(function (contribution) { return getContributionDisplayName(contribution) !== ""; });
    }, [contributions]);
    var publishers = useMemo(function () {
        return (title.titlePublishers || [])
            .map(function (titlePublisher) {
            var _a, _b;
            var name = titlePublisher.publisherName ||
                ((_a = titlePublisher.publisher) === null || _a === void 0 ? void 0 : _a.name) ||
                "";
            if (!name) {
                return "";
            }
            var role = (_b = titlePublisher.role) === null || _b === void 0 ? void 0 : _b.description;
            return role ? "".concat(name, " (").concat(role, ")") : name;
        })
            .filter(Boolean);
    }, [title.titlePublishers]);
    var publicationPlaces = useMemo(function () {
        return (title.publicationPlaces || [])
            .map(function (place) { return place.city || ""; })
            .filter(Boolean);
    }, [title.publicationPlaces]);
    var languages = useMemo(function () {
        return (title.languages || [])
            .map(function (titleLanguage) {
            var _a, _b;
            var language = (_a = titleLanguage.language) === null || _a === void 0 ? void 0 : _a.description;
            if (!language) {
                return "";
            }
            var role = (_b = titleLanguage.role) === null || _b === void 0 ? void 0 : _b.description;
            return role ? "".concat(language, " (").concat(role, ")") : language;
        })
            .filter(Boolean);
    }, [title.languages]);
    var collections = useMemo(function () {
        return (title.titleCollections || [])
            .map(function (titleCollection) {
            var _a;
            var name = ((_a = titleCollection.collection) === null || _a === void 0 ? void 0 : _a.mainName)
                ? formatCollectionOptionLabel(titleCollection.collection, intl.formatMessage)
                : "";
            if (!name) {
                return "";
            }
            return titleCollection.partNumber
                ? "".concat(name, ", ").concat(titleCollection.partNumber)
                : name;
        })
            .filter(Boolean);
    }, [title.titleCollections, intl]);
    var audiences = useMemo(function () {
        return (title.audiences || [])
            .map(function (audience) { var _a; return ((_a = audience.type) === null || _a === void 0 ? void 0 : _a.description) || ""; })
            .filter(Boolean);
    }, [title.audiences]);
    var subjects = useMemo(function () {
        return (title.subjects || [])
            .slice()
            .sort(function (a, b) {
            return Number(b.mainSubject || false) - Number(a.mainSubject || false);
        })
            .map(formatSubject)
            .filter(Boolean);
    }, [title.subjects]);
    var textContents = useMemo(function () {
        return (title.textContents || []).filter(function (textContent) { return !!textContent.text; });
    }, [title.textContents]);
    return (React.createElement(React.Fragment, null,
        isReadOnlyCoPublisher && (React.createElement(InfoBox, { type: InfoBoxTypeEnum.INFO, titleId: "ui-br-agency.title.detail.coPublisherReadOnly.title" },
            React.createElement(FormattedMessage, { id: "ui-br-agency.title.detail.coPublisherReadOnly.text" }))),
        React.createElement(TitleIssnMediumWarning, { titleId: title.id, titleCollections: title.titleCollections }),
        React.createElement("div", { className: css.hero },
            React.createElement(TitleCoverImage, { key: coverUrl, coverUrl: coverUrl, titleName: (mainName === null || mainName === void 0 ? void 0 : mainName.name) || "" }),
            React.createElement("div", { className: css.heroMeta },
                React.createElement("h2", { className: css.titleName }, (mainName === null || mainName === void 0 ? void 0 : mainName.name) || ""),
                (mainName === null || mainName === void 0 ? void 0 : mainName.subtitle) && (React.createElement("div", { className: css.heroLine },
                    React.createElement("span", { className: css.heroLabel },
                        React.createElement(FormattedMessage, { id: "ui-br-agency.title.names.subtitle.label" }),
                        ":"),
                    " ",
                    mainName.subtitle)),
                (mainName === null || mainName === void 0 ? void 0 : mainName.partNumber) && (React.createElement("div", { className: css.heroLine },
                    React.createElement("span", { className: css.heroLabel },
                        React.createElement(FormattedMessage, { id: "ui-br-agency.title.names.partNumber.label" }),
                        ":"),
                    " ",
                    mainName.partNumber)),
                (mainName === null || mainName === void 0 ? void 0 : mainName.partName) && (React.createElement("div", { className: css.heroLine },
                    React.createElement("span", { className: css.heroLabel },
                        React.createElement(FormattedMessage, { id: "ui-br-agency.title.names.partName.label" }),
                        ":"),
                    " ",
                    mainName.partName)),
                bylineContributions.length > 0 && (React.createElement("div", { className: css.heroLine },
                    React.createElement("span", { className: css.heroLabel },
                        React.createElement(FormattedMessage, { id: "ui-br-agency.title.detail.contributors.label" }),
                        ":"),
                    " ",
                    bylineContributions.map(function (contribution, index) {
                        var _a, _b, _c;
                        var displayName = getContributionDisplayName(contribution);
                        var personId = (_a = contribution.person) === null || _a === void 0 ? void 0 : _a.id;
                        var legalForm = (_b = contribution.person) === null || _b === void 0 ? void 0 : _b.legalForm;
                        var roles = (_c = contribution.roles) === null || _c === void 0 ? void 0 : _c.map(function (role) { var _a; return (_a = role.role) === null || _a === void 0 ? void 0 : _a.description; }).filter(Boolean).join(", ");
                        return (React.createElement("span", { key: contribution.id || index },
                            index > 0 && (React.createElement("span", { className: css.bylineSeparator }, "\u00B7")),
                            personId && legalForm ? (React.createElement(TextLink, { onClick: function () { return handlePersonClick(personId, legalForm); } }, displayName)) : (displayName),
                            roles && (React.createElement("span", { className: css.bylineRole },
                                " (",
                                roles,
                                ")"))));
                    }))))),
        React.createElement(DetailSection, { labelId: "ui-br-agency.title.detail.bibliography.label" },
            React.createElement("div", { className: css.factGrid },
                React.createElement(Fact, { labelId: "ui-br-agency.product.titleFolioId.label", value: title.id ? (React.createElement(React.Fragment, null,
                        React.createElement(CopyableId, { value: title.id }),
                        React.createElement(PublicCatalogLink, { record: title, notPublicMessageId: "ui-br-agency.publicCatalog.title.notPublic" }))) : undefined }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.owner.field.label", value: (_b = title.owner) === null || _b === void 0 ? void 0 : _b.name }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.publishers.table.title", value: publishers.length > 0
                        ? publishers.map(function (publisher, index) { return (React.createElement("div", { key: index }, publisher)); })
                        : undefined }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.publicationPlaces.field.label", value: publicationPlaces.length > 0
                        ? publicationPlaces.join(", ")
                        : undefined }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.languages.field.label", value: languages.length > 0 ? languages.join(", ") : undefined }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.collections.table.title", value: collections.length > 0 ? collections.join("; ") : undefined }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.editionNumber.field.label", value: (_c = title.editionNumber) === null || _c === void 0 ? void 0 : _c.toString() }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.editionStatement.field.label", value: title.editionStatement }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.audiences.field.label", value: audiences.length > 0 ? audiences.join(", ") : undefined }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.ageFrom.field.label", value: (_d = title.ageFrom) === null || _d === void 0 ? void 0 : _d.toString() }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.ageTo.field.label", value: (_e = title.ageTo) === null || _e === void 0 ? void 0 : _e.toString() }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.imprint.field.label", value: title.imprint }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.illustrationsNote.field.label", value: title.illustrationsNote }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.doi.field.label", value: title.doi ? (React.createElement("a", { href: title.doi, target: "_blank", rel: "noopener noreferrer" }, title.doi)) : undefined }),
                React.createElement(Fact, { labelId: "ui-br-agency.title.commentForAgency.field.label", value: title.commentForAgency, wide: true }))),
        otherNames.length > 0 && (React.createElement(DetailSection, { labelId: "ui-br-agency.title.detail.otherNames.label", count: otherNames.length },
            React.createElement(TitleNamesTable, { names: otherNames }))),
        contributions.length > 0 && (React.createElement(DetailSection, { labelId: "ui-br-agency.title.contributions.table.title", count: contributions.length },
            React.createElement(TitleContributionsTable, { contributions: contributions }))),
        subjects.length > 0 && (React.createElement(DetailSection, { labelId: "ui-br-agency.title.subjects.field.label" },
            React.createElement("div", { className: css.subjectList }, subjects.join(" · ")))),
        textContents.length > 0 && (React.createElement(DetailSection, { labelId: "ui-br-agency.title.textContents.field.label" }, textContents.map(function (textContent, index) {
            var _a;
            return (React.createElement("div", { key: textContent.id || index, className: css.textContentBlock },
                ((_a = textContent.type) === null || _a === void 0 ? void 0 : _a.description) && (React.createElement("div", { className: css.factLabel }, textContent.type.description)),
                React.createElement("p", { className: css.textContentText }, textContent.text),
                textContent.textAuthor && (React.createElement("div", { className: css.textContentAuthor }, textContent.textAuthor))));
        }))),
        supportingResources.length > 0 && (React.createElement(DetailSection, { labelId: "ui-br-agency.title.supportingResources.field.label", count: supportingResources.length },
            React.createElement(TitleSupportingResourcesTable, { resources: supportingResources }))),
        React.createElement(DetailSection, { labelId: "ui-br-agency.title.products.table.title" },
            React.createElement(TitleProductsPanel, { title: title }))));
};
export default TitleDetail;
